import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const RESOURCE_OPTIONS = [
  { v: "", l: "All resources" },
  { v: "patient", l: "Patients" },
  { v: "file", l: "Files (imaging)" },
  { v: "case_analysis", l: "Case analyses" },
  { v: "consultation", l: "Consultations" },
  { v: "review", l: "Specialist reviews" },
  { v: "auth", l: "Authentication" },
  { v: "user", l: "Users" },
  { v: "audit", l: "Audit reads" },
];

const STATUS_BADGE = {
  success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/40",
  failure: "bg-red-500/10 text-red-400 border-red-500/40",
};

export default function AdminAuditLogs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [filters, setFilters] = useState({
    user_email: "",
    resource_type: "",
    action: "",
    status: "",
    date_from: "",
    date_to: "",
  });

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin access required");
      nav("/dashboard");
    }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const r = await api.get("/admin/audit-logs", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (_) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const applyFilters = () => {
    setOffset(0);
    load();
  };

  const resetFilters = () => {
    setFilters({
      user_email: "",
      resource_type: "",
      action: "",
      status: "",
      date_from: "",
      date_to: "",
    });
    setOffset(0);
    setTimeout(load, 0);
  };

  return (
    <Layout>
      <div className="flex items-center gap-3">
        <ShieldCheck size={22} className="text-[var(--pa-gold)]" />
        <div>
          <div className="kicker">HIPAA Audit Trail</div>
          <h1
            className="font-serif text-3xl text-[var(--pa-text)]"
            data-testid="admin-audit-title"
          >
            Audit logs
          </h1>
        </div>
      </div>
      <p className="mt-3 text-xs text-[var(--pa-text-dim)] max-w-3xl leading-relaxed">
        Append-only record of every read, write, export, and access event on
        patient + case data. Each entry is timestamped (UTC), captures the
        acting user, IP, and the resource touched. Logs are write-only via
        application code; no UI surface deletes or mutates rows.
      </p>

      {/* Filters */}
      <div
        className="mt-6 p-4 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg grid sm:grid-cols-3 gap-3"
        data-testid="audit-filters"
      >
        <div>
          <label className="kicker block mb-1">User email contains</label>
          <input
            data-testid="audit-filter-email"
            value={filters.user_email}
            onChange={(e) =>
              setFilters({ ...filters, user_email: e.target.value })
            }
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          />
        </div>
        <div>
          <label className="kicker block mb-1">Resource</label>
          <select
            data-testid="audit-filter-resource"
            value={filters.resource_type}
            onChange={(e) =>
              setFilters({ ...filters, resource_type: e.target.value })
            }
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          >
            {RESOURCE_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="kicker block mb-1">Action prefix</label>
          <input
            data-testid="audit-filter-action"
            placeholder="e.g. file.download"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          />
        </div>
        <div>
          <label className="kicker block mb-1">Status</label>
          <select
            data-testid="audit-filter-status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          >
            <option value="">Any</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
          </select>
        </div>
        <div>
          <label className="kicker block mb-1">From (ISO date)</label>
          <input
            type="date"
            data-testid="audit-filter-from"
            value={filters.date_from}
            onChange={(e) =>
              setFilters({ ...filters, date_from: e.target.value })
            }
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          />
        </div>
        <div>
          <label className="kicker block mb-1">To (ISO date)</label>
          <input
            type="date"
            data-testid="audit-filter-to"
            value={filters.date_to}
            onChange={(e) =>
              setFilters({ ...filters, date_to: e.target.value })
            }
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-sm text-[var(--pa-text)] p-2 focus:outline-none focus:border-[var(--pa-gold)]"
          />
        </div>
        <div className="sm:col-span-3 flex items-center gap-2">
          <button
            onClick={applyFilters}
            data-testid="audit-filter-apply"
            className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold text-sm px-4 py-2 rounded hover:bg-[var(--pa-gold-hover)] transition"
          >
            Apply filters
          </button>
          <button
            onClick={resetFilters}
            data-testid="audit-filter-reset"
            className="text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] px-3 py-2"
          >
            Reset
          </button>
          <button
            onClick={load}
            data-testid="audit-refresh"
            className="ml-auto flex items-center gap-1.5 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)]"
            title="Refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 text-xs text-[var(--pa-text-dim)]">
        Showing <span className="text-[var(--pa-text)]">{items.length}</span> of{" "}
        <span className="text-[var(--pa-text)]">{total}</span> events
        {offset > 0 && <> · page offset {offset}</>}
      </div>

      <div
        className="mt-3 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg overflow-x-auto"
        data-testid="audit-table-wrap"
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-[var(--pa-text-muted)]">
            <Loader2 size={16} className="animate-spin text-[var(--pa-gold)]" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[var(--pa-text-muted)] text-sm">
            No audit events match these filters.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="audit-table">
            <thead>
              <tr className="text-left text-[var(--pa-text-dim)] uppercase tracking-wider border-b border-[var(--pa-border)]">
                <th className="p-3 font-semibold">Timestamp (UTC)</th>
                <th className="p-3 font-semibold">User</th>
                <th className="p-3 font-semibold">Action</th>
                <th className="p-3 font-semibold">Resource</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">IP</th>
                <th className="p-3 font-semibold">Metadata</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`audit-row-${it.id}`}
                  className="border-b border-[var(--pa-border)]/60 hover:bg-[var(--pa-bg-2)]/40"
                >
                  <td className="p-3 font-mono text-[10px] text-[var(--pa-text-muted)]">
                    {it.ts.replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="p-3">
                    <div className="text-[var(--pa-text)]">{it.user_email}</div>
                    {it.user_is_admin && (
                      <div className="text-[9px] uppercase tracking-wider text-[var(--pa-gold)]">
                        admin
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-mono text-[var(--pa-text)]">
                    {it.action}
                  </td>
                  <td className="p-3">
                    <div className="text-[var(--pa-text-muted)]">
                      {it.resource_type}
                    </div>
                    {it.resource_id && (
                      <div className="font-mono text-[9px] text-[var(--pa-text-dim)] truncate max-w-[10rem]">
                        {it.resource_id}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full border text-[10px] ${
                        STATUS_BADGE[it.status] ||
                        "bg-gray-500/10 text-gray-400 border-gray-500/40"
                      }`}
                    >
                      {it.status}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-[10px] text-[var(--pa-text-muted)]">
                    {it.ip}
                  </td>
                  <td className="p-3 font-mono text-[10px] text-[var(--pa-text-dim)] max-w-[18rem] truncate">
                    {Object.keys(it.metadata || {}).length > 0
                      ? JSON.stringify(it.metadata)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-xs">
        <button
          onClick={() => setOffset(Math.max(0, offset - limit))}
          disabled={offset === 0}
          data-testid="audit-prev"
          className="px-3 py-1.5 rounded border border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:border-[var(--pa-gold)] hover:text-[var(--pa-gold)] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          ← Prev
        </button>
        <span className="text-[var(--pa-text-dim)]">
          Showing {offset + 1}–{offset + items.length} of {total}
        </span>
        <button
          onClick={() => setOffset(offset + limit)}
          disabled={offset + items.length >= total}
          data-testid="audit-next"
          className="px-3 py-1.5 rounded border border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:border-[var(--pa-gold)] hover:text-[var(--pa-gold)] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Next →
        </button>
      </div>
    </Layout>
  );
}
